(() => {
  'use strict';
  if (customElements.get('mb-brand-film')) return;

  class MartenbrownBrandFilm extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.initialize(), {
          once: true, signal: this.controller.signal,
        });
      } else this.initialize();
    }

    initialize() {
      if (!this.isConnected || this.video) return;
      this.video = this.querySelector('video');
      if (!this.video) return;
      const options = { signal: this.controller.signal };
      this.words = {
        play: this.dataset.playLabel, pause: this.dataset.pauseLabel,
        unmute: this.dataset.unmuteLabel, mute: this.dataset.muteLabel,
        blocked: this.dataset.blockedLabel, failed: this.dataset.failedLabel,
      };
      this.poster = this.querySelector('[data-film-poster]');
      this.status = this.querySelector('[data-film-status]');
      this.playButton = this.querySelector('[data-film-play]');
      this.soundButton = this.querySelector('[data-film-sound]');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (typeof this.wantsPlay !== 'boolean') {
        this.wantsPlay = this.dataset.autoplay === 'true' && !this.reducedMotion.matches;
      }
      const bounds = this.getBoundingClientRect();
      this.inView = bounds.bottom > 0 && bounds.top < window.innerHeight;
      this.loaded = false;
      this.playAttempt = null;
      this.video.defaultMuted = true;
      this.video.muted = true;
      this.video.controls = false;
      const controls = this.querySelector('.mb-brand-film__controls');
      if (controls) controls.hidden = false;

      this.playButton?.addEventListener('click', () => {
        this.wantsPlay = !this.playAttempt && this.video.paused;
        this.syncPlayback();
      }, options);
      this.soundButton?.addEventListener('click', () => {
        const enable = this.video.muted;
        this.video.muted = !enable;
        if (enable && !this.hasEnabledSound) {
          if (this.loaded) this.video.currentTime = 0;
          this.hasEnabledSound = true;
        }
        this.wantsPlay = true;
        this.syncPlayback();
      }, options);
      for (const type of ['play', 'playing']) {
        this.video.addEventListener(type, () => {
          // Some browsers fulfill play() after a tab, modal or observer has
          // already paused the film. Do not let that late start escape the gate.
          if (!this.canPlay()) this.video.pause();
          else if (type === 'playing') {
            if (this.poster) this.poster.hidden = true;
            this.setStatus('');
          }
          this.paint();
        }, options);
      }
      for (const type of ['pause', 'volumechange', 'ended']) {
        this.video.addEventListener(type, () => this.paint(), options);
      }
      this.video.addEventListener('error', () => {
        this.wantsPlay = false;
        this.playAttempt = null;
        if (this.poster) this.poster.hidden = false;
        this.setStatus(this.words.failed);
        this.syncPlayback();
      }, options);
      document.addEventListener('mb:store-notice-state', () => this.syncPlayback(), options);
      document.addEventListener('visibilitychange', () => this.syncPlayback(), options);
      this.reducedMotion.addEventListener('change', () => {
        if (this.reducedMotion.matches) {
          this.wantsPlay = false;
          this.syncPlayback();
        }
      }, options);
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          this.inView = entries[0].isIntersecting;
          this.syncPlayback();
        }, { threshold: 0.1 });
        this.observer.observe(this);
      }
      this.syncPlayback();
    }

    canPlay() {
      const notice = document.getElementById('mb-store-notice');
      // Unknown state blocks too: the deferred notice script may not have run.
      const noticeBlocks = notice && (notice.open || notice.dataset.mbNoticeState !== 'dismissed');
      return this.isConnected && this.wantsPlay && this.inView && !document.hidden && !noticeBlocks;
    }

    syncPlayback() {
      const video = this.video;
      if (!video) return;
      if (!this.canPlay()) {
        this.playAttempt = null;
        video.pause();
        this.paint();
        return;
      }
      if (!this.loaded) {
        video.querySelectorAll('source[data-film-src]').forEach(source => {
          source.src = source.dataset.filmSrc;
        });
        this.loaded = true;
        video.load();
      } else if (video.error) video.load();
      if (this.playAttempt || !video.paused) {
        this.paint();
        return;
      }
      const attempt = { video };
      this.playAttempt = attempt;
      this.setStatus('');
      const failed = (error) => {
        if (this.playAttempt !== attempt) return;
        this.playAttempt = null;
        if (!this.isConnected || this.video !== video || !this.canPlay()) {
          video.pause();
          return;
        }
        if (error.name !== 'AbortError') {
          this.wantsPlay = false;
          this.setStatus(error.name === 'NotAllowedError' ? this.words.blocked : this.words.failed);
        }
        this.paint();
      };
      try {
        Promise.resolve(video.play()).then(() => {
          if (this.playAttempt === attempt) this.playAttempt = null;
          if (!this.isConnected || this.video !== video || !this.canPlay()) video.pause();
          if (this.video === video) this.paint();
        }, failed);
      } catch (error) { failed(error); }
      this.paint();
    }

    setStatus(message) {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.hidden = !message;
    }

    paint() {
      if (!this.video) return;
      const playing = (!this.video.paused && !this.video.ended) || Boolean(this.playAttempt);
      const playLabel = playing ? this.words.pause : this.words.play;
      this.playButton?.setAttribute('aria-label', playLabel);
      this.playButton?.setAttribute('title', playLabel);
      this.querySelector('[data-film-play-icon]')?.toggleAttribute('hidden', playing);
      this.querySelector('[data-film-pause-icon]')?.toggleAttribute('hidden', !playing);
      const soundLabel = this.video.muted ? this.words.unmute : this.words.mute;
      this.soundButton?.setAttribute('aria-label', soundLabel);
      this.soundButton?.setAttribute('title', soundLabel);
      this.soundButton?.setAttribute('aria-pressed', String(!this.video.muted));
      this.querySelector('[data-film-mute-icon]')?.toggleAttribute('hidden', !this.video.muted);
      this.querySelector('[data-film-audio-icon]')?.toggleAttribute('hidden', this.video.muted);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.controller?.abort();
      this.playAttempt = null;
      this.video?.pause();
      this.controller = null;
      this.video = null;
    }
  }
  customElements.define('mb-brand-film', MartenbrownBrandFilm);
})();
